import { useRef, useState } from "react";
import { bestandsDeltas, jahrgangTitel } from "../kern/fachlogik.js";
import { fmtZahl, jahrLabel, parseJahr, parseZahl } from "../kern/format.js";
import { EIGENE_KRITIK, FLASCHENGROESSEN, NV, SCHEMATA, ZUCKER_KATEGORIEN } from "../kern/konstanten.js";
import { Auswahl, Eingabe, Feld, ModalFuss, TextLoeschen, Textbereich, VorschlagEingabe } from "../ui/primitive.jsx";
import { t } from "../i18n/index.js";


function JahrgangForm({ objekt, vorschlaege, katalogJahrgaenge = [], onSave, onCancel }) {
  const istNV = objekt ? objekt.jahr === NV : false;
  const [w, setW] = useState({
    nv: istNV,
    jahr: objekt && objekt.jahr !== NV ? String(objekt.jahr) : "",
    alkohol: objekt?.alkohol ?? "", saeure: objekt?.saeure ?? "",
    restzucker: objekt?.restzucker ?? "", ph: objekt?.ph ?? "",
    zucker: objekt?.zucker ?? "",
    schwefel: objekt?.schwefel ?? "", kapsel: objekt?.kapsel ?? "",
    ausbau: objekt?.ausbau ?? "", verschluss: objekt?.verschluss ?? "",
    preis: objekt?.preis ?? "", startbestand: "",
    lagerort: objekt?.lagerort ?? "",
    trinkVon: objekt?.trinkVon != null ? String(objekt.trinkVon) : "",
    trinkBis: objekt?.trinkBis != null ? String(objekt.trinkBis) : "",
  });
  const [fehler, setFehler] = useState(null);
  const setze = (k) => (e) => setW({ ...w, [k]: e.target.value });

  /* ── Übernahme aus dem gemeinsamen Katalog ─────────────────────

     Das Jahr steuert sie, nicht ein Feld davor: Wer einen Jahrgang
     anlegt, hat das Jahr im Kopf — kennt der Katalog es für diesen
     Wein, wird dessen Fassung genommen. Vorher musste man erst eine
     Auswahlliste bedienen, und danach war das Jahrfeld gesperrt.

     Der Jahrgang behält die Kennung aus dem Katalog. Das ist der
     eigentliche Gewinn: Er bleibt damit verknüpft und bekommt
     spätere Änderungen am Trinkfenster oder Etikett mit, statt als
     zweiter Jahrgang desselben Jahres daneben zu stehen.

     Übernommen werden Trinkfenster, Etikett und die Analysewerte
     (Alkohol, Säure, Restzucker, pH, Schwefel) — sie stehen auf dem
     Etikett und gehören damit zum Wein. Bestand, Lagerort und Preis
     bleiben beim Nutzer; die gehören zur eigenen Flasche.

     Eigene Eingaben werden nicht überschrieben: Wer bereits einen
     Wert eingetragen hat, behält ihn. */
  const jahrZahl = w.nv ? NV : parseInt(w.jahr, 10);
  const gewaehlt = katalogJahrgaenge.find((j) => j.jahr === jahrZahl) || null;

  /* Trinkfenster wird beim Wechsel des Jahres gesetzt, nicht bei
     jedem Rendern — sonst überschriebe es eine Eingabe des Nutzers
     immer wieder. */
  /* Welche Felder aus dem Katalog gefüllt wurden und nicht vom
     Nutzer stammen. Nur diese dürfen bei einem Jahrwechsel wieder
     ersetzt oder geleert werden.

     Ohne diese Unterscheidung ginge es in beide Richtungen schief:
     Überschreibt man immer, ist eine eigene Messung weg. Füllt man
     nur Leeres, bleibt beim Wechsel von 2015 auf 2019 die Säure des
     2015ers stehen — ein fremder Wert im gespeicherten Jahrgang, und
     niemand sieht ihm an, woher er kommt. */
  const ausKatalogGefuellt = useRef(new Set());

  const KAT_FELDER = ["trinkVon", "trinkBis", "alkohol", "saeure", "restzucker", "ph", "schwefel"];

  const ausKatalog = (v, kj) => {
    const raus = {};
    const gefuellt = ausKatalogGefuellt.current;
    for (const f of KAT_FELDER) {
      const eigen = String(v[f] ?? "").trim();
      /* Eine Eingabe des Nutzers bleibt unangetastet. */
      if (eigen !== "" && !gefuellt.has(f)) continue;
      const kw = kj ? kj[f] : null;
      if (kw != null && String(kw).trim() !== "") {
        raus[f] = String(kw);
        gefuellt.add(f);
      } else if (gefuellt.has(f)) {
        /* Der neue Jahrgang sagt dazu nichts — dann darf der Wert des
           alten auch nicht stehen bleiben. */
        raus[f] = "";
        gefuellt.delete(f);
      }
    }
    return raus;
  };

  /* Tippt der Nutzer selbst, gehört das Feld ihm. */
  const setzeEigen = (k) => (e) => {
    ausKatalogGefuellt.current.delete(k);
    setW((v) => ({ ...v, [k]: e.target.value }));
  };

  const uebernimm = (kj) => setW((v) => ({ ...v, ...ausKatalog(v, kj) }));

  const setzeJahr = (e) => {
    const roh = e.target.value;
    const kj = katalogJahrgaenge.find((j) => j.jahr === parseInt(roh, 10)) || null;
    setW((v) => ({ ...v, jahr: roh, ...ausKatalog(v, kj) }));
  };

  const waehleJahr = (kj) => {
    setW((v) => ({
      ...v,
      nv: kj.jahr === NV,
      jahr: kj.jahr === NV ? "" : String(kj.jahr),
      ...ausKatalog(v, kj),
    }));
  };

  const setzeNV = (an) => {
    const kj = an ? (katalogJahrgaenge.find((j) => j.jahr === NV) || null) : null;
    setW((v) => ({ ...v, nv: an, ...ausKatalog(v, kj) }));
  };

  const speichern = () => {
    let jahr;
    if (w.nv) {
      jahr = NV;
    } else {
      jahr = parseInt(w.jahr, 10);
      const aktuellesJahr = new Date().getFullYear();
      if (!jahr || jahr < 1900 || jahr > aktuellesJahr + 1)
        return setFehler(`Bitte einen Jahrgang zwischen 1900 und ${aktuellesJahr + 1} angeben — oder „Ohne Jahrgang (N.V.)" wählen.`);
    }
    const ph = parseZahl(w.ph);
    if (ph != null && (ph < 2.4 || ph > 4.5))
      return setFehler("Der pH-Wert liegt außerhalb des önologisch plausiblen Bereichs (2,4–4,5).");
    const werte = {
      jahr,
      alkohol: parseZahl(w.alkohol), saeure: parseZahl(w.saeure),
      restzucker: parseZahl(w.restzucker), ph,
      zucker: String(w.zucker).trim(),
      schwefel: String(w.schwefel).trim(), kapsel: String(w.kapsel).trim(),
      ausbau: String(w.ausbau).trim(), verschluss: String(w.verschluss).trim(),
      preis: parseZahl(w.preis),
      lagerort: String(w.lagerort).trim(),
      trinkVon: parseJahr(w.trinkVon), trinkBis: parseJahr(w.trinkBis),
    };
    if (werte.trinkVon != null && werte.trinkBis != null && werte.trinkBis < werte.trinkVon)
      return setFehler("Das Trinkfenster endet vor seinem Beginn — bitte von/bis prüfen.");
    if (!objekt) {
      const sb = parseInt(w.startbestand, 10);
      werte.bestaende = { "0.75": Number.isNaN(sb) || sb < 0 ? 0 : sb };
      /* Aus dem Katalog übernommen: ID und Etikett mitnehmen. */
      if (gewaehlt) {
        werte.id = gewaehlt.id;
        werte.katalogFoto = gewaehlt.foto || null;
      }
    }
    const err = onSave(werte);
    if (err) setFehler(err);
  };

  return (
    <>
      <div className="wl-formgitter">
        <Feld label={t("Jahrgang")}>
          <Eingabe value={w.nv ? "" : w.jahr} onChange={setzeJahr} inputMode="numeric" autoFocus
            disabled={w.nv} placeholder={w.nv ? "Ohne Jahrgang (N.V.)" : "z. B. 2022"} />
          <label className="wl-check wl-nvschalter">
            <input type="checkbox" checked={w.nv}
              onChange={(e) => setzeNV(e.target.checked)} />
            Ohne Jahrgang (N.V.)
          </label>

          {/* Jahre, die der Katalog für diesen Wein kennt und die hier
              noch fehlen. Ein Griff statt Tippen — und die Übernahme
              geschieht dabei von selbst. */}
          {!objekt && katalogJahrgaenge.length > 0 && (
            <div className="wl-jgkatalog">
              <span className="wl-jgkatalog-titel">{t("Im Katalog verfügbar")}</span>
              <div className="wl-jgkatalog-jahre">
                {katalogJahrgaenge.map((j) => (
                  <button key={j.id} type="button"
                    className={"wl-jgkatalog-jahr" + (gewaehlt && gewaehlt.id === j.id ? " ist-gewaehlt" : "")}
                    onClick={() => waehleJahr(j)}>
                    {jahrLabel(j.jahr)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Was die Übernahme bewirkt, gehört sichtbar hin — sonst
              wundert man sich über ein Trinkfenster, das man nicht
              eingetragen hat. */}
          {!objekt && gewaehlt && (
            <p className="wl-jgkatalog-hinweis">
              {t("Aus dem Katalog übernommen:")}{" "}
              {[
                gewaehlt.trinkVon != null || gewaehlt.trinkBis != null
                  ? `${t("Trinkfenster")} ${gewaehlt.trinkVon != null ? gewaehlt.trinkVon : "?"}–${gewaehlt.trinkBis != null ? gewaehlt.trinkBis : "?"}`
                  : null,
                [["alkohol", "Alkohol"], ["saeure", "Säure"], ["restzucker", "Restzucker"],
                 ["ph", "pH"], ["schwefel", "Schwefel"]]
                  .filter(([f]) => gewaehlt[f] != null && String(gewaehlt[f]).trim() !== "")
                  .map(([, label]) => t(label)).join(", ") || null,
                gewaehlt.foto ? t("Etikett") : null,
              ].filter(Boolean).join(" · ") || t("Verknüpfung mit dem Katalog")}
            </p>
          )}
        </Feld>
        <Feld label={t("Alkohol (% vol)")}><Eingabe value={w.alkohol} onChange={setzeEigen("alkohol")} inputMode="decimal" placeholder="z. B. 12,5" /></Feld>
        <Feld label={t("Zuckerkategorie")}>
          <Auswahl value={w.zucker} onChange={setze("zucker")}>
            <option value="">— keine Angabe —</option>
            {ZUCKER_KATEGORIEN.map((z) => <option key={z} value={z}>{z}</option>)}
          </Auswahl>
        </Feld>
        <Feld label={t("Säure (g/l)")}><Eingabe value={w.saeure} onChange={setzeEigen("saeure")} inputMode="decimal" /></Feld>
        <Feld label={t("Restzucker (g/l)")}><Eingabe value={w.restzucker} onChange={setzeEigen("restzucker")} inputMode="decimal" /></Feld>
        <Feld label="pH-Wert"><Eingabe value={w.ph} onChange={setzeEigen("ph")} inputMode="decimal" placeholder="z. B. 3,25" /></Feld>
        <Feld label={t("Verschluss")}><VorschlagEingabe vorschlaege={vorschlaege ? vorschlaege.verschluss : []} value={w.verschluss} onChange={setze("verschluss")} placeholder="z. B. Naturkork" /></Feld>
        <Feld label={t("Schwefel")}><Eingabe value={w.schwefel} onChange={setzeEigen("schwefel")} placeholder="z. B. 45 mg/l gesamt" /></Feld>
        <Feld label={t("Kapsel")}><VorschlagEingabe vorschlaege={vorschlaege ? vorschlaege.kapsel : []} value={w.kapsel} onChange={setze("kapsel")} placeholder="z. B. Zinn, Wachs, ohne" /></Feld>
        <Feld label={t("Ausbau")} breit><Eingabe value={w.ausbau} onChange={setze("ausbau")} placeholder="z. B. 18 Monate Barrique, 25 % neu" /></Feld>
        <Feld label={t("Preis (€ / Flasche)")}><Eingabe value={w.preis} onChange={setze("preis")} inputMode="decimal" /></Feld>
        {!objekt && (
          <Feld label={t("Anfangsbestand (0,75-l-Flaschen)")}>
            <Eingabe value={w.startbestand} onChange={setze("startbestand")} inputMode="numeric" placeholder="0" />
          </Feld>
        )}
        <Feld label={t("Lagerort")}><VorschlagEingabe vorschlaege={vorschlaege ? vorschlaege.lagerort : []} value={w.lagerort} onChange={setze("lagerort")} placeholder="z. B. Regal B1" /></Feld>
        <Feld label={t("Trinkfenster von")}><Eingabe value={w.trinkVon} onChange={setzeEigen("trinkVon")} inputMode="numeric" placeholder="z. B. 2026" /></Feld>
        <Feld label={t("Trinkfenster bis")}><Eingabe value={w.trinkBis} onChange={setzeEigen("trinkBis")} inputMode="numeric" placeholder="z. B. 2032" /></Feld>
      </div>
      {fehler && <p className="wl-fehler">{fehler}</p>}
      <ModalFuss onCancel={onCancel} onSave={speichern} />
    </>
  );
}


function KritikForm({ objekt, wein, vorwahlJahr, vorwahlJgId, nachEntnahme = false, vorschlaege, kritikPrefs = null, onSave, onCancel, onDelete = null }) {
  const jahrgaenge = [...wein.jahrgaenge].sort((a, b) => (b.jahr || 0) - (a.jahr || 0));
  const startJgId = objekt?.jgId
    ?? vorwahlJgId
    ?? (vorwahlJahr != null ? (wein.jahrgaenge.find((j) => j.jahr === vorwahlJahr) || {}).id : null)
    ?? (jahrgaenge[0] ? jahrgaenge[0].id : "");
  const [w, setW] = useState({
    jgId: startJgId,
    /* „Eigene Kritik“ ist der Regelfall — wer eine fremde Quelle
       zitiert, überschreibt es. Beim Bearbeiten bleibt stehen, was
       dort steht. */
    kritiker: objekt?.kritiker ?? EIGENE_KRITIK,
    schema: objekt?.schema ?? (kritikPrefs?.schema || "p100"),
    wert: objekt?.wert ?? "",
    anlass: objekt?.anlass ?? "",
    pairing: objekt?.pairing ?? "",
    text: objekt?.text ?? "",
  });
  const [fehler, setFehler] = useState(null);
  const setze = (k) => (e) => setW({ ...w, [k]: e.target.value });
  const schema = SCHEMATA[w.schema];

  const speichern = () => {
    if (!w.jgId) return setFehler("Bitte zuerst einen Jahrgang für diesen Wein anlegen.");
    if (!w.kritiker.trim()) return setFehler("Bitte den Kritiker bzw. die Quelle angeben.");
    const wert = parseZahl(w.wert);
    if (wert == null || wert < schema.min || wert > schema.max)
      return setFehler(`Der Wert muss zwischen ${fmtZahl(schema.min, 2)} und ${fmtZahl(schema.max, 2)} liegen (${schema.label}).`);
    const jg = wein.jahrgaenge.find((j) => j.id === w.jgId);
    const err = onSave({
      jgId: w.jgId, jahr: jg ? jg.jahr : NV, kritiker: w.kritiker.trim(),
      schema: w.schema, wert, anlass: w.anlass.trim(), pairing: w.pairing.trim(), text: w.text.trim(),
    });
    if (err) setFehler(err);
  };

  if (jahrgaenge.length === 0) {
    return (
      <>
        <p className="wl-hinweis">{t("Für diesen Wein existiert noch kein Jahrgang. Lege zuerst einen Jahrgang an, um Kritiken zu erfassen.")}</p>
        <ModalFuss onCancel={onCancel} onSave={onCancel} saveLabel={t("Verstanden")} />
      </>
    );
  }

  return (
    <>
      <p className="wl-hinweis">{wein.name}{nachEntnahme && " · Entnahme verbucht — halte den Eindruck der Flasche direkt fest."}</p>
      <div className="wl-formgitter">
        <Feld label={t("Jahrgang")}>
          <Auswahl value={w.jgId} onChange={setze("jgId")}>
            {jahrgaenge.map((jg) => <option key={jg.id} value={jg.id}>{jahrgangTitel(wein, jg)}</option>)}
          </Auswahl>
        </Feld>
        <Feld label={t("Kritiker / Quelle")}><VorschlagEingabe vorschlaege={vorschlaege ? vorschlaege.kritiker : []} value={w.kritiker} onChange={setze("kritiker")} placeholder="z. B. Jancis Robinson" /></Feld>
        <Feld label={t("Bewertungsschema")}>
          <Auswahl value={w.schema} onChange={setze("schema")}>
            {Object.entries(SCHEMATA).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
          </Auswahl>
        </Feld>
        <Feld label={`Wert (${fmtZahl(schema.min, 2)}–${fmtZahl(schema.max, 2)})`}>
          <Eingabe value={w.wert} onChange={setze("wert")} inputMode="decimal" />
        </Feld>
        <Feld label={t("Anlass")}><VorschlagEingabe vorschlaege={vorschlaege ? vorschlaege.anlass : []} value={w.anlass} onChange={setze("anlass")} placeholder="z. B. Geburtstag, Blindprobe" /></Feld>
        <Feld label={t("Speisenbegleitung (Pairing)")}><VorschlagEingabe vorschlaege={vorschlaege ? vorschlaege.pairing : []} value={w.pairing} onChange={setze("pairing")} placeholder="z. B. Reh, Hartkäse" /></Feld>
        <Feld label={t("Verkostungsnotiz")} breit><Textbereich value={w.text} onChange={setze("text")} rows={3} /></Feld>
      </div>
      {fehler && <p className="wl-fehler">{fehler}</p>}
      <ModalFuss
        onCancel={onCancel} onSave={speichern}
        cancelLabel={nachEntnahme ? "Ohne Kritik schließen" : "Abbrechen"}
        saveLabel={t("Kritik speichern")}
        links={onDelete ? <TextLoeschen onDelete={onDelete} /> : null}
      />
    </>
  );
}



/** Entnahme aus dem Bestand — im Anschluss wird direkt die
    Kritik-Erfassung zum entnommenen Jahrgang angeboten. */
function EntnahmeForm({ jahrgang, onSave, onCancel }) {
  const verfuegbar = FLASCHENGROESSEN.filter((g) => ((jahrgang.bestaende || {})[g.key] || 0) > 0);
  const [w, setW] = useState({ groesse: verfuegbar[0] ? verfuegbar[0].key : "0.75", menge: 1 });
  const [fehler, setFehler] = useState(null);

  if (verfuegbar.length === 0) {
    return (
      <>
        <p className="wl-hinweis">{t("Für diesen Jahrgang ist kein Bestand vorhanden — buche zuerst einen Zugang.")}</p>
        <ModalFuss onCancel={onCancel} onSave={onCancel} saveLabel={t("Verstanden")} />
      </>
    );
  }

  const speichern = () => {
    const menge = parseInt(w.menge, 10);
    const bestand = (jahrgang.bestaende || {})[w.groesse] || 0;
    const groesse = FLASCHENGROESSEN.find((g) => g.key === w.groesse);
    if (!menge || menge < 1) return setFehler("Bitte eine Menge von mindestens 1 angeben.");
    if (menge > bestand) return setFehler(`Nur ${bestand} × ${groesse ? groesse.kurz : w.groesse} im Bestand.`);
    const err = onSave({ groesse: w.groesse, menge });
    if (err) setFehler(err);
  };

  return (
    <>
      <p className="wl-hinweis">{t("Nach der Entnahme kannst du direkt eine Verkostungsnotiz zum Jahrgang erfassen.")}</p>
      <div className="wl-formgitter">
        <Feld label={t("Flaschengröße")}>
          <Auswahl value={w.groesse} onChange={(e) => setW({ ...w, groesse: e.target.value })}>
            {verfuegbar.map((g) => {
              const n = (jahrgang.bestaende || {})[g.key] || 0;
              return <option key={g.key} value={g.key}>{g.label} — {n} im Bestand</option>;
            })}
          </Auswahl>
        </Feld>
        <Feld label={t("Menge")}>
          <Eingabe value={w.menge} onChange={(e) => setW({ ...w, menge: e.target.value })} inputMode="numeric" autoFocus />
        </Feld>
      </div>
      {fehler && <p className="wl-fehler">{fehler}</p>}
      <ModalFuss onCancel={onCancel} onSave={speichern} saveLabel={t("Entnehmen")} />
    </>
  );
}


/**
 * Bestehende Ein- oder Auslagerung bearbeiten. Der Bestand wird beim
 * Speichern um die Differenz nachgeführt, damit er zur Historie passt.
 * Bei einer Entnahme begrenzt der Bestand die zulässige Erhöhung.
 */
function BewegungForm({ objekt, jahrgang, vorschlaege, onSave, onCancel, onDelete }) {
  const istZugang = objekt.art === "zugang";
  const [w, setW] = useState({
    groesse: objekt.groesse || "0.75",
    menge: String(objekt.menge != null ? objekt.menge : 1),
    haendler: objekt.haendler || "",
    preis: objekt.preis != null ? String(objekt.preis).replace(".", ",") : "",
    datum: objekt.datum ? new Date(objekt.datum).toISOString().slice(0, 10) : "",
  });
  const [fehler, setFehler] = useState(null);

  const speichern = () => {
    const menge = parseInt(w.menge, 10);
    if (!menge || menge < 1) return setFehler("Bitte eine Menge von mindestens 1 angeben.");
    /* Die Änderung darf den Bestand nicht ins Minus drücken — weder
       durch eine größere Entnahme noch durch einen verkleinerten
       Zugang, dessen Flaschen längst getrunken sind. Beides würde sonst
       stillschweigend auf 0 gekappt und die Zahlen verfälschen. */
    const kurz = (key) => {
      const g = FLASCHENGROESSEN.find((x) => x.key === key);
      return g ? g.kurz : key;
    };
    const deltas = bestandsDeltas(objekt, { ...objekt, groesse: w.groesse, menge });
    for (const [groesse, delta] of deltas) {
      const bestand = (jahrgang.bestaende || {})[groesse] || 0;
      if (bestand + delta < 0) {
        /* Wie weit die Buchung ins Minus liefe — daraus die Grenze:
           bei einem Zugang das Minimum, bei einer Entnahme das Maximum. */
        const zuviel = -(bestand + delta);
        return setFehler(istZugang
          ? `Von diesem Zugang sind bereits Flaschen entnommen — weniger als ${menge + zuviel} × ${kurz(groesse)} lässt sich nicht buchen.`
          : `Höchstens ${menge - zuviel} × ${kurz(groesse)} möglich.`);
      }
    }
    const err = onSave({
      groesse: w.groesse, menge,
      haendler: w.haendler.trim(),
      preis: parseZahl(w.preis),
      datum: w.datum ? new Date(w.datum).toISOString() : objekt.datum,
    });
    if (err) setFehler(err);
  };

  return (
    <>
      <p className="wl-hinweis">
        {istZugang
          ? "Änderungen werden dem Bestand gutgeschrieben bzw. abgezogen."
          : "Änderungen werden dem Bestand wieder gutgeschrieben bzw. abgezogen. Eine zugehörige Kritik bleibt erhalten."}
      </p>
      <div className="wl-formgitter">
        <Feld label={t("Flaschengröße")}>
          <Auswahl value={w.groesse} onChange={(e) => setW({ ...w, groesse: e.target.value })}>
            {FLASCHENGROESSEN.map((g) => <option key={g.key} value={g.key}>{g.label}</option>)}
          </Auswahl>
        </Feld>
        <Feld label={t("Menge")}>
          <Eingabe value={w.menge} onChange={(e) => setW({ ...w, menge: e.target.value })} inputMode="numeric" autoFocus />
        </Feld>
        <Feld label={t("Datum")}>
          <Eingabe type="date" value={w.datum} onChange={(e) => setW({ ...w, datum: e.target.value })} />
        </Feld>
        <Feld label={istZugang ? "Kaufpreis je Flasche (€)" : "Wert je Flasche (€)"}>
          <Eingabe value={w.preis} onChange={(e) => setW({ ...w, preis: e.target.value })} inputMode="decimal" placeholder="optional" />
        </Feld>
        {istZugang && (
          <Feld label={t("Bezugsquelle")} breit>
            <VorschlagEingabe vorschlaege={vorschlaege ? vorschlaege.haendler : []} value={w.haendler}
              onChange={(e) => setW({ ...w, haendler: e.target.value })} placeholder="z. B. Weinhandlung, Winzer direkt" />
          </Feld>
        )}
      </div>
      {fehler && <p className="wl-fehler">{fehler}</p>}
      <ModalFuss onCancel={onCancel} onSave={speichern}
        links={<TextLoeschen onDelete={onDelete} />} />
    </>
  );
}


/** Zugang zum Bestand einer wählbaren Flaschengröße. */
function ZugangForm({ jahrgang, vorschlaege, onSave, onCancel }) {
  const [w, setW] = useState({
    groesse: "0.75", menge: 1,
    haendler: "",
    preis: jahrgang.preis != null ? String(jahrgang.preis).replace(".", ",") : "",
    datum: new Date().toISOString().slice(0, 10),
  });
  const [fehler, setFehler] = useState(null);

  const speichern = () => {
    const menge = parseInt(w.menge, 10);
    if (!menge || menge < 1) return setFehler("Bitte eine Menge von mindestens 1 angeben.");
    const err = onSave({
      groesse: w.groesse, menge,
      haendler: w.haendler.trim(),
      preis: parseZahl(w.preis),
      datum: w.datum || null,
    });
    if (err) setFehler(err);
  };

  return (
    <>
      <p className="wl-hinweis">{t("Der Zugang wird dem Bestand der gewählten Flaschengröße gutgeschrieben. Bezugsquelle und Kaufpreis fließen in die Kaufhistorie ein.")}</p>
      <div className="wl-formgitter">
        <Feld label={t("Flaschengröße")}>
          <Auswahl value={w.groesse} onChange={(e) => setW({ ...w, groesse: e.target.value })}>
            {FLASCHENGROESSEN.map((g) => {
              const n = (jahrgang.bestaende || {})[g.key] || 0;
              return <option key={g.key} value={g.key}>{g.label} — {n} im Bestand</option>;
            })}
          </Auswahl>
        </Feld>
        <Feld label={t("Menge")}>
          <Eingabe value={w.menge} onChange={(e) => setW({ ...w, menge: e.target.value })} inputMode="numeric" autoFocus />
        </Feld>
        <Feld label={t("Bezugsquelle")}>
          <VorschlagEingabe vorschlaege={vorschlaege ? vorschlaege.haendler : []} value={w.haendler}
            onChange={(e) => setW({ ...w, haendler: e.target.value })} placeholder="z. B. Weinhandlung, Winzer direkt" />
        </Feld>
        <Feld label={t("Kaufpreis je Flasche (€)")}>
          <Eingabe value={w.preis} onChange={(e) => setW({ ...w, preis: e.target.value })} inputMode="decimal" placeholder="optional" />
        </Feld>
        <Feld label={t("Kaufdatum")}>
          <Eingabe type="date" value={w.datum} onChange={(e) => setW({ ...w, datum: e.target.value })} />
        </Feld>
      </div>
      {fehler && <p className="wl-fehler">{fehler}</p>}
      <ModalFuss onCancel={onCancel} onSave={speichern} saveLabel={t("Zugang buchen")} />
    </>
  );
}

export { BewegungForm, EntnahmeForm, JahrgangForm, KritikForm, ZugangForm };
